import { getB2bCustomer, getB2bCustomerCatalog, listB2bCustomers, placeB2bOrderOnBehalf, quoteB2bOrder } from '@api/b2b';
import PageHeader from '@components/common/PageHeader';
import { Button, Select } from '@mantine/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import OrderForm from './OrderForm';
import styles from './b2b.module.css';

/** The brand placing an order for a customer (phone, WhatsApp, a sales visit). Same form as the portal. */
const NewB2bOrderPage: React.FC = () => {
    const navigate = useNavigate();
    const { data: customers = [] } = useQuery({ queryKey: ['b2b-customers'], queryFn: listB2bCustomers });
    const [customerId, setCustomerId] = useState<string | null>(null);
    const { data: customer } = useQuery({ queryKey: ['b2b-customer', customerId], queryFn: () => getB2bCustomer(customerId as string), enabled: !!customerId });
    const { data: catalog } = useQuery({ queryKey: ['b2b-catalog', customerId], queryFn: () => getB2bCustomerCatalog(customerId as string), enabled: !!customerId });
    const quote = useCallback((lines: Parameters<typeof quoteB2bOrder>[1], picks: Parameters<typeof quoteB2bOrder>[2]) => quoteB2bOrder(customerId as string, lines, picks), [customerId]);

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <div className={styles.between}>
                <PageHeader icon="🧾" title="New B2B order" subtitle="Place an order on a customer's behalf. It goes to review like any portal order." />
                <Button variant={ButtonVariant.DEFAULT} leftSection={<ArrowLeftIcon size={16} />} onClick={() => void navigate('/sales-orders')}>
                    Sales orders
                </Button>
            </div>
            <Select
                label="Customer"
                data={customers.filter((c) => c.status === 'ACTIVE').map((c) => ({ value: c.customerId, label: `${c.name}${c.segmentLabel ? ` · ${c.segmentLabel}` : ''}` }))}
                value={customerId}
                onChange={setCustomerId}
                searchable
                w={360}
                comboboxProps={{ position: 'bottom-start', width: 'target' }}
            />
            {customerId && customer && catalog ? (
                <OrderForm
                    key={customerId}
                    catalog={catalog}
                    shipTos={customer.shipTos}
                    quote={quote}
                    placeLabel="Place order for customer"
                    onBehalf
                    customerName={customer.name}
                    onPlace={async (input) => {
                        const order = await placeB2bOrderOnBehalf({ customerId, ...input });
                        void navigate(`/sales-orders/${encodeURIComponent(order.purchaseOrderId)}`);
                    }}
                />
            ) : customerId ? (
                <span className={styles.muted}>Loading the customer's catalogue…</span>
            ) : null}
        </div>
    );
};

export default NewB2bOrderPage;
